import { useQuery } from '@tanstack/react-query'
import { bff } from '../../api/client'
import { BFF_PATHS, FORMIO } from '../../config/constants'
import { env } from '../../config/env'
import { useAuth } from '../../auth/authContext'
import { tagLoanproDocKeys } from '../../formio/loanproStorage'
import type { KycFormResponse, FormSchema, KycStatusDefinitions } from '../../types'

type FormioComponent = {
  type?: string
  hidden?: boolean
  conditional?: unknown
  customConditional?: string
  validate?: { required?: boolean; [k: string]: unknown }
  components?: FormioComponent[]
  [k: string]: unknown
}

/**
 * ⚠️ DEV TOGGLE — flip to `true` to force every KYC panel visible, `false` for the real flow.
 *
 * Historically the reveal was gated on Form.io's private-doc verify (which 403'd);
 * conditionals are now rewritten server-side to key off staff-review flags in
 * `submission.data`, so this may be obsolete — kept until that's confirmed against
 * the live flow, then remove together with devUnlockForm.ts.
 *
 * Compiled in only when `VITE_UNLOCK_ALL_SECTIONS=true` at build time. Local
 * `.env.local` and the dev-site workflow set it. The production deploy does not,
 * so that build keeps the real section gates and this module is tree-shaken out.
 */
const UNLOCK_ALL_SECTIONS = import.meta.env.VITE_UNLOCK_ALL_SECTIONS === 'true'

export function stabilizeComponentCalculations(component: FormioComponent): void {
  // Disable Form.io debounce so the math resolves instantly on every keystroke
  component.debounce = 0
  if (component.redrawOn === 'undefined') component.redrawOn = ''
  if (component.refreshOn === 'undefined') component.refreshOn = ''

  if (typeof component.calculateValue === 'string' && component.calculateValue) {
    if (component.key === 'Total' || component.key === 'lineTotal') {
      component.calculateValue = `
        var currentRow = instance.data || row || {};
        var qty = Number(currentRow.quantity ?? currentRow.unit) || 0;
        var cost = Number(currentRow.unitPrice ?? currentRow.costUnit) || 0;
        var calculated = Math.round(qty * cost * 100) / 100;
        if (Number(value) !== calculated) {
          value = calculated;
        }
      `;
    } else if (component.key === 'totalInUsd' || component.key === 'totalInUSD') {
      component.calculateValue = `
        var currentRow = instance.data || row || {};
        var qty = Number(currentRow.quantity ?? currentRow.unit) || 0;
        var cost = Number(currentRow.unitPrice ?? currentRow.costUnit) || 0;
        var lineTotal = (currentRow.lineTotal != null || currentRow.Total != null)
          ? (Number(currentRow.lineTotal ?? currentRow.Total) || 0)
          : (qty * cost);
        var rate = Number(data.localExchangeRate ?? data.exchangeRate) || 1;
        var calculated = Math.round((lineTotal / rate) * 100) / 100;
        if (Number(value) !== calculated) {
          value = calculated;
        }
      `;
    } else if (component.key === 'totalBudget' || component.key === 'totalBudgetInUSD') {
      component.calculateValue = `
        var DNB_FEE_USD = 200;
        var budgetList = data.grantBudget || data.GrantBudget || [];
        var lineItems = budgetList.reduce(function(sum, r) {
          return sum + (Number(r.totalInUSD ?? r.totalInUsd ?? r.Total) || 0);
        }, 0);
        var calculated = Math.round((lineItems + DNB_FEE_USD) * 100) / 100;
        if (Number(value) !== calculated) {
          value = calculated;
        }
      `;
    }
  }

  // Recurse into nested components, columns, and rows
  if (Array.isArray(component.components)) {
    for (const child of component.components) {
      stabilizeComponentCalculations(child as FormioComponent)
    }
  }
  if (Array.isArray(component.columns)) {
    for (const col of component.columns) {
      if (Array.isArray((col as FormioComponent).components)) {
        for (const child of (col as FormioComponent).components!) {
          stabilizeComponentCalculations(child as FormioComponent)
        }
      }
    }
  }
  if (Array.isArray(component.rows)) {
    for (const row of component.rows) {
      if (Array.isArray(row)) {
        for (const cell of row) {
          if (Array.isArray((cell as FormioComponent).components)) {
            for (const child of (cell as FormioComponent).components!) {
              stabilizeComponentCalculations(child as FormioComponent)
            }
          }
        }
      }
    }
  }
}

export function stabilizeFormCalculations(form: FormSchema): FormSchema {
  const clone = structuredClone(form)
  if (Array.isArray(clone.components)) {
    for (const component of clone.components) {
      stabilizeComponentCalculations(component as FormioComponent)
    }
  }
  return clone
}

/**
 * Fetches the KYC form **and** the caller's review state through the BFF
 * (`GET /me/kyc-form`, authenticated): `form` arrives with its conditionals
 * pre-rewired to the review flags in `submission.data`, so the page renders
 * `form` seeded with `submission` and merges nothing else. Each loanpro file
 * component is tagged with its key as `dir` so uploads carry the docKey the
 * BFF requires (see tagLoanproDocKeys).
 */
export function useApplicationForm() {
  const { authenticated, claims } = useAuth()
  // Keep one user's rewritten schema from being served to the next user in this tab.
  const userKey = claims?.assetId || claims?.email || null
  return useQuery({
    // The user and the unlock toggle are part of the key so a change busts the
    // cached schema and refetches, instead of serving the previous shape for
    // the staleTime window.
    queryKey: ['kyc-form', userKey, UNLOCK_ALL_SECTIONS],
    enabled: authenticated,
    // Infinity would otherwise keep the first review state for the whole session.
    // 'always' refetches when /apply mounts, so a decision made elsewhere shows
    // up on the next visit. Submit still invalidates this query immediately.
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    queryFn: async () => {
      try {
        const res = await bff<KycFormResponse>(BFF_PATHS.kycForm)
        if (!res?.form?.components) {
          throw new Error(`Malformed response from ${BFF_PATHS.kycForm}: missing form`)
        }
        // Fresh response, not yet in the query cache — safe to tag in place.
        tagLoanproDocKeys(res.form.components)

        // Globally stabilize calculations for BFF form to prevent loops in production
        const stabilizedForm = stabilizeFormCalculations(res.form)

        // Included only when the build sets VITE_UNLOCK_ALL_SECTIONS. See above.
        if (UNLOCK_ALL_SECTIONS) {
          const { devUnlockForm } = await import('./devUnlockForm')
          return { ...res, form: devUnlockForm(stabilizedForm) }
        }
        return { ...res, form: stabilizedForm }
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn('BFF fetch failed. Falling back to direct Form.io schema fetch for local testing.', err)
          const formioUrl = `${env.VITE_FORMIO_URL}/form/${FORMIO.applicationFormId}`
          const formRes = await fetch(formioUrl)
          if (!formRes.ok) {
            throw new Error('BFF failed (' + (err as Error).message + '), and direct Form.io fetch failed with status ' + formRes.status, { cause: err })
          }
          const formSchema = await formRes.json()
          tagLoanproDocKeys(formSchema.components)

          // Globally stabilize calculations for fallback direct fetch
          const stabilizedSchema = stabilizeFormCalculations(formSchema)

          const mockRes: KycFormResponse = {
            caller: { assetId: 'mock-dev-asset-id', email: 'mock-dev@example.com' },
            form: stabilizedSchema,
            submission: { data: {} },
            kyc: {
              kycStatus: 'in-progress',
              sections: {},
              mapping: {
                version: 1,
                caseStatus: 'in-progress',
                caseStatusLabel: 'In progress',
                sectionOrder: [],
                sections: {},
                documentToSection: {},
                statusDefinitions: { case: {}, section: {}, document: {} } as unknown as KycStatusDefinitions
              }
            }
          }

          if (UNLOCK_ALL_SECTIONS) {
            const { devUnlockForm } = await import('./devUnlockForm')
            return { ...mockRes, form: devUnlockForm(stabilizedSchema) }
          }
          return mockRes
        }
        throw err
      }
    },
  })
}
